'use client';
/**
 * components/QRSheet.tsx — OGRYN
 *
 * Bottom sheet QR code :
 *   1. Scanner un QR code (caméra live, BarcodeDetector + fallback jsQR CDN)
 *   2. QR permanent (profil prestataire)
 *   3. QR éphémère de l'intervention en cours
 *
 * Le scanner s'ouvre à l'intérieur de la sheet (sheet grandit),
 * pas en plein écran.
 */

import { useState, useEffect, useRef, useCallback, type CSSProperties } from 'react';
import { Camera, X, RefreshCw, User, Zap } from 'lucide-react';
import { useMobile } from '@/contexts/MobileContext';
import { trpc } from '@/lib/trpc/client';
import { useCompte } from '@/lib/auth/client';

// BarcodeDetector — API Web expérimentale
declare class BarcodeDetector {
  constructor(options?: { formats?: string[] });
  detect(source: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement): Promise<Array<{ rawValue: string; format: string }>>;
  static getSupportedFormats(): Promise<string[]>;
}

// ─── jsQR via CDN (fallback BarcodeDetector) ─────────────────────────────────

function loadJsQR(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).jsQR) { resolve(); return; }
    const s = document.createElement('script');
    s.src = 'https://unpkg.com/jsqr@1.4.0/dist/jsQR.js';
    s.onload  = () => resolve();
    s.onerror = () => reject(new Error('jsQR load failed'));
    document.head.appendChild(s);
  });
}

// ─── QR visuel minimal ────────────────────────────────────────────────────────

const PATTERN_PROFIL = [
  1,1,1,0,1,1,1,
  1,0,1,0,1,0,1,
  1,1,1,0,1,1,1,
  0,1,0,1,0,1,0,
  1,1,0,1,0,0,1,
  1,0,1,0,1,1,0,
  1,1,1,0,1,0,1,
];

const PATTERN_INTERV = PATTERN_PROFIL.map((v, i) => (i % 5 === 0 ? 1 - v : v));

function QRGrid({ size, color, pattern }: { size: number; color: string; pattern: number[] }) {
  return (
    <div style={{
      width: size, height: size,
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: 1, padding: 6, borderRadius: 10,
    }}>
      {pattern.map((v, i) => (
        <div key={i} style={{ borderRadius: 1, background: v ? color : 'rgba(255,255,255,.04)' }} />
      ))}
    </div>
  );
}

// ─── Scanner inline ───────────────────────────────────────────────────────────

type ScanState = 'requesting' | 'scanning' | 'success' | 'error';

interface InlineScannerProps {
  onClose: () => void;
  onDetect?: (value: string) => void;
}

function InlineScanner({ onClose, onDetect }: InlineScannerProps) {
  const [state,   setState]   = useState<ScanState>('requesting');
  const [message, setMessage] = useState('');
  const [result,  setResult]  = useState('');

  const videoRef    = useRef<HTMLVideoElement>(null);
  const canvasRef   = useRef<HTMLCanvasElement>(null);
  const streamRef   = useRef<MediaStream | null>(null);
  const detectorRef = useRef<InstanceType<typeof BarcodeDetector> | null>(null);
  const rafRef      = useRef<number>(0);

  const stopCamera = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const handleResult = useCallback((value: string) => {
    stopCamera();
    setResult(value);
    setState('success');
    onDetect?.(value);
  }, [stopCamera, onDetect]);

  const startCamera = useCallback(async () => {
    setState('requesting');
    setMessage('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      if ('BarcodeDetector' in window) {
        // ── Méthode 1 : BarcodeDetector natif ─────────────────────────────
        detectorRef.current = new BarcodeDetector({ formats: ['qr_code'] });
        setState('scanning');
        const scan = async () => {
          if (!videoRef.current || !detectorRef.current) return;
          try {
            const codes = await detectorRef.current.detect(videoRef.current);
            if (codes.length > 0) { handleResult(codes[0].rawValue); return; }
          } catch { /* continue */ }
          rafRef.current = requestAnimationFrame(scan);
        };
        rafRef.current = requestAnimationFrame(scan);
      } else {
        // ── Méthode 2 : jsQR via CDN (fallback Safari / iOS < 17) ─────────
        try {
          await loadJsQR();
        } catch {
          setState('error');
          setMessage('Scanner non disponible sur ce navigateur');
          return;
        }
        setState('scanning');
        const canvas = canvasRef.current;
        const ctx    = canvas?.getContext('2d');
        const scan   = () => {
          const video = videoRef.current;
          if (!video || !canvas || !ctx) return;
          const { videoWidth: w, videoHeight: h } = video;
          if (w > 0 && h > 0) {
            canvas.width = w; canvas.height = h;
            ctx.drawImage(video, 0, 0, w, h);
            const imageData = ctx.getImageData(0, 0, w, h);
            const code = (window as any).jsQR(imageData.data, imageData.width, imageData.height, {
              inversionAttempts: 'dontInvert',
            });
            if (code) { handleResult(code.data); return; }
          }
          rafRef.current = requestAnimationFrame(scan);
        };
        rafRef.current = requestAnimationFrame(scan);
      }
    } catch {
      setState('error');
      setMessage('Accès à la caméra refusé ou non disponible');
    }
  }, [handleResult]);

  const reset = useCallback(() => {
    setResult('');
    setMessage('');
    startCamera();
  }, [startCamera]);

  useEffect(() => {
    startCamera();
    return stopCamera;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes rpScanLine { 0%,100%{top:10%} 50%{top:84%} }
        @keyframes rpSpin { to{transform:rotate(360deg)} }
      `}} />

      {/* Canvas caché pour jsQR */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Header scanner */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
          <div style={{ width:30, height:30, borderRadius:8, background:'rgba(5,150,105,.15)', border:'1px solid rgba(5,150,105,.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Camera size={14} color="#2E7D52" />
          </div>
          <div>
            <div style={{ color:'#fff', fontWeight:700, fontSize:13 }}>Scanner</div>
            <div style={{ color:'rgba(255,255,255,.35)', fontSize:10 }}>
              {state === 'requesting' && 'Accès caméra…'}
              {state === 'scanning'   && 'Détection automatique'}
              {state === 'success'    && 'QR code détecté !'}
              {state === 'error'      && 'Erreur'}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:5 }}>
          {state === 'success' && (
            <button type="button" onClick={reset} style={{ width:28, height:28, borderRadius:7, background:'rgba(255,255,255,.07)', border:'0.5px solid rgba(255,255,255,.12)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <RefreshCw size={12} color="rgba(255,255,255,.5)" />
            </button>
          )}
          <button type="button" onClick={onClose} style={{ width:28, height:28, borderRadius:7, background:'rgba(255,255,255,.07)', border:'0.5px solid rgba(255,255,255,.12)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <X size={12} color="rgba(255,255,255,.5)" />
          </button>
        </div>
      </div>

      {/* Viewfinder — carré, pleine largeur */}
      <div style={{ position:'relative', width:'100%', aspectRatio:'1', borderRadius:14, overflow:'hidden', background:'rgba(0,0,0,.6)', marginBottom:10 }}>
        {/* Flux vidéo */}
        <video
          ref={videoRef}
          playsInline muted autoPlay
          style={{
            width:'100%', height:'100%', objectFit:'cover',
            display: (state === 'success' || state === 'error') ? 'none' : 'block',
          }}
        />

        {/* Coins du cadre */}
        {state === 'scanning' && (['tl','tr','bl','br'] as const).map(c => (
          <div key={c} style={{
            position:'absolute', width:22, height:22,
            borderColor:'#2E7D52', borderStyle:'solid',
            borderTopWidth:    c.startsWith('t') ? '3px' : '0',
            borderBottomWidth: c.startsWith('b') ? '3px' : '0',
            borderLeftWidth:   c.endsWith('l')   ? '3px' : '0',
            borderRightWidth:  c.endsWith('r')   ? '3px' : '0',
            top:    c.startsWith('t') ? 8 : undefined,
            bottom: c.startsWith('b') ? 8 : undefined,
            left:   c.endsWith('l')   ? 8 : undefined,
            right:  c.endsWith('r')   ? 8 : undefined,
            borderRadius:
              c === 'tl' ? '5px 0 0 0' :
              c === 'tr' ? '0 5px 0 0' :
              c === 'bl' ? '0 0 0 5px' : '0 0 5px 0',
          }} />
        ))}

        {/* Ligne scan */}
        {state === 'scanning' && (
          <div style={{
            position:'absolute', left:'10%', right:'10%', height:2,
            background:'linear-gradient(90deg, transparent, #2E7D52, transparent)',
            animation:'rpScanLine 2s ease-in-out infinite',
            boxShadow:'0 0 6px rgba(5,150,105,.5)',
          }} />
        )}

        {/* requesting */}
        {state === 'requesting' && (
          <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10 }}>
            <div style={{ width:32, height:32, border:'2.5px solid #2E7D52', borderTopColor:'transparent', borderRadius:'50%', animation:'rpSpin .8s linear infinite' }} />
            <span style={{ color:'rgba(255,255,255,.4)', fontSize:12 }}>Accès à la caméra…</span>
          </div>
        )}

        {/* success */}
        {state === 'success' && (
          <div style={{ position:'absolute', inset:0, background:'rgba(22,163,74,.12)', border:'1px solid rgba(22,163,74,.35)', borderRadius:14, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, padding:16 }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M8 12l3 3 5-6" /></svg>
            <div style={{ color:'#86efac', fontWeight:700, fontSize:14 }}>QR scanné !</div>
            <div style={{ color:'rgba(255,255,255,.4)', fontSize:10, textAlign:'center', wordBreak:'break-all', maxWidth:220 }}>{result}</div>
          </div>
        )}

        {/* error */}
        {state === 'error' && (
          <div style={{ position:'absolute', inset:0, background:'rgba(220,38,38,.08)', border:'1px solid rgba(220,38,38,.25)', borderRadius:14, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, padding:16 }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fca5a5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg>
            <div style={{ color:'#fca5a5', fontSize:12, textAlign:'center' }}>{message}</div>
            <button type="button" onClick={reset} style={{ padding:'7px 16px', borderRadius:8, background:'#2E7D52', border:'none', color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer' }}>
              Réessayer
            </button>
          </div>
        )}
      </div>

      {/* Hint */}
      {state === 'scanning' && (
        <p style={{ margin:'0 0 10px', textAlign:'center', color:'rgba(255,255,255,.25)', fontSize:11 }}>
          Placez un QR code OGRYN dans le cadre — détection auto
        </p>
      )}
    </>
  );
}

// ─── Sheet principale ─────────────────────────────────────────────────────────

type Selected = 'profil' | 'intervention';

export default function QRSheet() {
  const { qrOpen, setQrOpen } = useMobile();
  const { user }              = useCompte();
  const [selected,    setSelected]    = useState<Selected>('profil');
  const [scannerOpen, setScannerOpen] = useState(false);

  const { data: activeInterv } = trpc.interventions.list.useQuery(
    { status: 'in_progress', limit: 1 },
    { enabled: qrOpen && !!user },
  );
  const intervention = activeInterv?.interventions?.[0] ?? null;

  useEffect(() => {
    if (!qrOpen) {
      setSelected('profil');    // eslint-disable-line react-hooks/set-state-in-effect
      setScannerOpen(false);     
    }
  }, [qrOpen]);

  if (!qrOpen) return null;

  // Le nom d'enseigne vivait dans les métadonnées Clerk, recopiées par un
  // webhook ; il est désormais lu où il a toujours été écrit — le profil
  // prestataire — et le nom du compte sert de repli.
  const prestataireName = user?.name || 'Prestataire';

  const optStyle = (key: Selected, accentColor: string): CSSProperties => ({
    flex:         selected === key ? 3 : 1,
    background:   selected === key ? `${accentColor}0D` : 'rgba(255,255,255,.03)',
    border:       `1.5px solid ${selected === key ? `${accentColor}66` : 'rgba(255,255,255,.09)'}`,
    borderRadius: 14,
    padding:      '10px 8px',
    cursor:       'pointer',
    transition:   'all .22s ease',
    overflow:     'hidden',
    display:      'flex',
    flexDirection: 'column',
    alignItems:   'center',
  });

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setQrOpen(false)}
        style={{ position:'fixed', inset:0, bottom:'56px', background:'rgba(0,0,0,.55)', zIndex:900 }}
      />

      {/* Bulle popup — au-dessus des FABs */}
      <div style={{
        position:   'fixed',
        bottom:     'calc(116px + env(safe-area-inset-bottom))',
        left:       12, right: 12,
        background: 'rgba(10,6,3,.98)',
        borderRadius: 18,
        border:     '0.5px solid rgba(255,255,255,.09)',
        zIndex:     901,
        padding:    '12px 14px 14px',
        maxHeight:  scannerOpen ? 'calc(82dvh - 120px)' : 'calc(55dvh - 120px)',
        overflowY:  'auto',
        scrollbarWidth: 'none',
        transition: 'max-height .3s cubic-bezier(.32,.72,0,1)',
        boxShadow: '0 8px 40px rgba(0,0,0,.7)',
      }}>

        {/* Scanner inline — affiché quand actif */}
        {scannerOpen && (
          <InlineScanner
            onClose={() => setScannerOpen(false)}
            onDetect={(val) => console.info('[QR]', val)}
          />
        )}

        {/* Bouton scanner — masqué quand actif */}
        {!scannerOpen && (
          <button
            type="button"
            onClick={() => setScannerOpen(true)}
            style={{
              width:'100%', height:40, borderRadius:10, marginBottom:10,
              background:'rgba(255,255,255,.06)',
              border:'0.5px solid rgba(255,255,255,.14)',
              color:'rgba(255,255,255,.7)', fontSize:12, fontWeight:600,
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:7,
            }}
          >
            <Camera size={14} color="rgba(255,255,255,.7)" />
            <span>Scanner un QR code</span>
          </button>
        )}

        {/* Options QR */}
        <div style={{ display:'flex', gap:10 }}>

          {/* ── Profil permanent ── */}
          <div style={optStyle('profil', '#2E7D52')} onClick={() => setSelected('profil')}>
            <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:8, alignSelf:'flex-start' }}>
              <User size={13} color="rgba(255,255,255,.6)" />
              <span style={{ fontSize:9, fontWeight:700, color:'rgba(255,255,255,.7)' }}>Profil permanent</span>
            </div>
            <div style={{ border:`2px solid rgba(5,150,105,.55)`, borderRadius:12, background:'rgba(0,0,0,.35)' }}>
              <QRGrid
                size={selected === 'profil' ? 130 : 60}
                color="rgba(5,150,105,.92)"
                pattern={PATTERN_PROFIL}
              />
            </div>
            {selected === 'profil' && (
              <div style={{ marginTop:8, fontSize:9, color:'rgba(255,255,255,.4)', textAlign:'center' }}>
                {prestataireName}
              </div>
            )}
          </div>

          {/* ── Intervention en cours ── */}
          <div style={optStyle('intervention', '#16A34A')} onClick={() => setSelected('intervention')}>
            <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:8, alignSelf:'flex-start' }}>
              <Zap size={13} color="rgba(255,255,255,.6)" />
              <span style={{ fontSize:9, fontWeight:700, color:'rgba(255,255,255,.7)' }}>
                {intervention ? `Intervention #${intervention.id.slice(-4).toUpperCase()}` : 'Aucune en cours'}
              </span>
            </div>
            {intervention ? (
              <>
                <div style={{ border:`2px solid rgba(22,163,74,.55)`, borderRadius:12, background:'rgba(0,0,0,.35)' }}>
                  <QRGrid
                    size={selected === 'intervention' ? 130 : 60}
                    color="rgba(22,163,74,.88)"
                    pattern={PATTERN_INTERV}
                  />
                </div>
                {selected === 'intervention' && (
                  <div style={{ marginTop:8, fontSize:9, color:'rgba(22,163,74,.7)', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" /></svg>
                    QR éphémère · expire bientôt
                  </div>
                )}
              </>
            ) : (
              <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'12px 0' }}>
                <span style={{ fontSize:9, color:'rgba(255,255,255,.25)', textAlign:'center' }}>
                  Aucune intervention{'\n'}active pour l'instant
                </span>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}
